/**
 * Cookie preferences modal: toggles for Necessary (disabled), Analytics, Functional, Marketing.
 * Save applies consent and updates tracking scripts immediately.
 */

import { useState } from 'react';
import { useConsent } from './useConsent';
import type { CookieConsent } from './types';
import { DEFAULT_CONSENT } from './types';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/cms/ui/dialog';
import { Switch } from '@/components/cms/ui/switch';
import LureoButton from '@/common/LureoButton';
import { Choice } from '@/components/ui/choice';

const CATEGORIES: { key: keyof Pick<CookieConsent, 'necessary' | 'analytics' | 'functional' | 'marketing'>; label: string; description: string; disabled?: boolean }[] = [
  { key: 'necessary', label: 'Necessary', description: 'Required for the site to work (e.g. sign-in, security). Cannot be disabled.', disabled: true },
  { key: 'analytics', label: 'Analytics', description: 'Helps us understand how you use the product (e.g. PostHog, Google Analytics).' },
  { key: 'functional', label: 'Functional', description: 'Enables extra features and preferences.' },
  { key: 'marketing', label: 'Marketing', description: 'Used for messaging and personalised content (e.g. Customer.io).' },
];

export function CookiePreferencesModal() {
  const { consent, preferencesOpen, setPreferencesOpen, setConsentAndApply } = useConsent();
  const [draft, setDraft] = useState<CookieConsent>(() => consent ?? DEFAULT_CONSENT);

  // The draft starts again from the saved consent whenever the preferences open, or the consent changes while open
  const source = preferencesOpen ? (consent ?? DEFAULT_CONSENT) : undefined;
  const [draftSource, setDraftSource] = useState(source);
  if (source !== draftSource) {
    setDraftSource(source);
    if (source) setDraft(source);
  }

  const handleToggle = (key: keyof CookieConsent, value: boolean) => {
    if (key === 'necessary') return;
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setConsentAndApply(draft);
    setPreferencesOpen(false);
  };

  return (
    <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
      <DialogContent
        width="md"
        data-cookie-consent
        aria-describedby="cookie-preferences-description"
      >
        <DialogHeader>
          <DialogTitle id="cookie-preferences-title">Cookie preferences</DialogTitle>
          <DialogDescription id="cookie-preferences-description">
            Choose which categories of cookies you allow. You can change this later from the footer.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {CATEGORIES.map(({ key, label, description, disabled }) => (
            <Choice
              key={key}
              layout="end"
              className="items-start gap-4 rounded-lureo-radius-lg border-lureo-border-width border-lureo-line p-3"
              control={
                <Switch
                  id={`cookie-${key}`}
                  checked={draft[key] as boolean}
                  disabled={disabled}
                  onCheckedChange={(checked) => handleToggle(key, checked)}
                  aria-label={`${label} cookies ${draft[key] ? 'on' : 'off'}`}
                />
              }
              label={label}
              description={description}
            />
          ))}
        </DialogBody>
        <DialogFooter>
          <LureoButton
            variant="outline-2"
            onClick={() => setPreferencesOpen(false)}
            className="h-9 rounded-lureo-radius-button-pill px-5"
            suppressTooltip
          >
            Cancel
          </LureoButton>
          <LureoButton
            variant="outline-3"
            onClick={handleSave}
            className="h-9 rounded-lureo-radius-button-pill px-4"
            suppressTooltip
          >
            Save preferences
          </LureoButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
